import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Play, Heart, ExternalLink } from 'lucide-react';

// HUD Corner SVG Component
const HUDCorner = ({ position }) => {
  const classes = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0 rotate-90',
    'bottom-left': 'bottom-0 left-0 -rotate-90',
    'bottom-right': 'bottom-0 right-0 rotate-180',
  };

  return (
    <svg
      className={`absolute w-3 h-3 text-vav-accent-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${classes[position]}`}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M0 16V4L4 0H16" />
    </svg>
  );
};

// Tech Label Component
export const TechLabel = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-vav-bg-subtle/80 border-vav-border text-vav-fg-muted',
    accent: 'bg-vav-accent-cyan/10 border-vav-accent-cyan/30 text-vav-accent-cyan',
    success: 'bg-vav-accent-green/10 border-vav-accent-green/30 text-vav-accent-green',
    warning: 'bg-vav-accent-orange/10 border-vav-accent-orange/30 text-vav-accent-orange',
    gold: 'bg-vav-gold/10 border-vav-gold/30 text-vav-gold',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono uppercase border backdrop-blur-sm ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// External store URL
const EXTERNAL_STORE_URL = 'https://www.taktikstore.com';

// Asymmetric Product Card with soft curves - Showcase mode (links to external store)
export const ProductCard = ({ product, index = 0, className = '' }) => {
  const {
    slug,
    name,
    price,
    original_price,
    discount_percent,
    images,
    brand,
    is_new,
    variants,
    videos,
    currency = 'TRY',
  } = product;

  const formatPrice = (p) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(p);
  };

  // Get unique colors from variants
  const colors = variants 
    ? [...new Map(variants.map(v => [v.color, { color: v.color, code: v.color_code, image: images[variants.indexOf(v) % images.length] }])).values()].slice(0, 4)
    : [];

  // Asymmetric border radius patterns
  const borderPatterns = [
    'rounded-[2rem_0.5rem_2rem_0.5rem]',
    'rounded-[0.5rem_2rem_0.5rem_2rem]',
    'rounded-[1.5rem_0.5rem_1.5rem_0.5rem]',
    'rounded-[0.5rem_1.5rem_0.5rem_1.5rem]',
  ];
  const borderPattern = borderPatterns[index % borderPatterns.length];

  const hasVideo = videos && videos.length > 0;
  const secondImage = images && images.length > 1 ? images[1] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <a
        href={`${EXTERNAL_STORE_URL}/urun/${slug}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`group block relative overflow-hidden bg-vav-bg-paper ${borderPattern} border border-vav-border hover:border-vav-accent-cyan/50 transition-all duration-500 ${className}`}
        data-testid={`product-card-${slug}`}
      >
        {/* HUD Corners */}
        <HUDCorner position="top-left" />
        <HUDCorner position="top-right" />
        <HUDCorner position="bottom-left" />
        <HUDCorner position="bottom-right" />

        {/* Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden">
          {/* Primary Image */}
          <img
            src={images?.[0] || '/placeholder.jpg'}
            alt={name}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
              secondImage ? 'group-hover:opacity-0 group-hover:scale-105' : 'group-hover:scale-105'
            }`}
            loading="lazy"
          />
          
          {/* Secondary Image (shown on hover) */}
          {secondImage && (
            <img
              src={secondImage}
              alt={`${name} - 2`}
              className="absolute inset-0 w-full h-full object-cover transition-all duration-500 opacity-0 group-hover:opacity-100 scale-100 group-hover:scale-105"
              loading="lazy"
            />
          )}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-vav-bg via-transparent to-transparent opacity-60" />
          
          {/* Overlay Labels */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {is_new && <TechLabel variant="accent">[YENİ]</TechLabel>}
            {discount_percent && (
              <TechLabel variant="warning">%{discount_percent}</TechLabel>
            )}
          </div>

          {/* Video indicator */}
          {hasVideo && (
            <div className="absolute top-3 right-3">
              <div className="w-8 h-8 rounded-full bg-vav-bg/80 backdrop-blur-sm flex items-center justify-center border border-vav-border">
                <Play className="w-4 h-4 text-vav-accent-cyan fill-vav-accent-cyan" />
              </div>
            </div>
          )}

          {/* Wishlist */}
          <motion.button
            className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-vav-bg/80 backdrop-blur-sm flex items-center justify-center border border-vav-border opacity-0 group-hover:opacity-100 transition-opacity"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.preventDefault();
              // TODO: Add to wishlist
            }}
          >
            <Heart className="w-5 h-5 text-vav-fg" />
          </motion.button>

          {/* Soft glow effect on hover - CSS based */}
          <div className="absolute inset-0 pointer-events-none transition-all duration-500 group-hover:shadow-[inset_0_0_60px_rgba(0,240,255,0.1)]" />
        </div>

        {/* Info Container */}
        <div className="p-4 space-y-3">
          {/* Brand */}
          <span className="text-xs font-mono text-vav-fg-muted uppercase tracking-wider">
            {brand}
          </span>

          {/* Name */}
          <h3 className="font-heading text-lg text-vav-fg group-hover:text-vav-accent-cyan transition-colors duration-300 line-clamp-2">
            {name}
          </h3>

          {/* Color Options */}
          {colors.length > 0 && (
            <div className="flex items-center gap-2">
              {colors.map((c, idx) => (
                <div
                  key={idx}
                  className="w-6 h-6 rounded-full border-2 border-vav-border overflow-hidden"
                  style={{ backgroundColor: c.code }}
                  title={c.color}
                />
              ))}
              {variants && [...new Set(variants.map(v => v.color))].length > 4 && (
                <span className="text-xs text-vav-fg-muted">
                  +{[...new Set(variants.map(v => v.color))].length - 4}
                </span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="flex items-baseline gap-2 pt-2 border-t border-vav-border/50">
            <span className="font-heading text-xl text-vav-fg">
              {formatPrice(price)}
            </span>
            {original_price && original_price > price && (
              <span className="text-sm text-vav-fg-dark line-through">
                {formatPrice(original_price)}
              </span>
            )}
          </div>
        </div>
      </a>
    </motion.div>
  );
};

// Feature Card Component (for landing page)
export const FeatureCard = ({ icon: Icon, title, description, label, color = '#c4a84b', className = '' }) => {
  return (
    <motion.div 
      className={`group relative p-6 bg-vav-bg-paper rounded-2xl border border-vav-border hover:border-vav-accent-cyan/50 transition-all duration-300 ${className}`}
      whileHover={{ y: -5 }}
    >
      <HUDCorner position="top-left" />
      <HUDCorner position="top-right" />

      {label && (
        <TechLabel variant="accent" className="mb-4">
          [{label}]
        </TechLabel>
      )}

      {Icon && (
        <div 
          className="w-14 h-14 flex items-center justify-center mb-4 rounded-xl border border-vav-border group-hover:border-current transition-colors"
          style={{ color }}
        >
          <Icon className="w-7 h-7" />
        </div>
      )}

      <h3 className="font-heading text-xl text-vav-fg mb-2 group-hover:text-vav-accent-cyan transition-colors">
        {title}
      </h3>

      <p className="text-vav-fg-muted text-sm leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
};

// Round Category Card with TARGET effect and Video support
export const CategoryCard = ({ name, slug, image, video_url, count, index = 0, external_url }) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-50px" });
  
  // Use external URL if provided, otherwise default to taktikstore
  const linkUrl = external_url || `https://www.taktikstore.com/kategori/${slug}`;

  // Play video on hover
  useEffect(() => {
    if (videoRef.current && video_url) {
      if (isHovered) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered, video_url]);
  
  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1, type: "spring" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex flex-col items-center text-center"
        data-testid={`category-card-${slug}`}
      >
        {/* Round Image Container with TARGET effect */}
        <motion.div
          className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mb-3 rounded-full overflow-hidden border-2 border-vav-border group-hover:border-vav-gold transition-all duration-300"
          whileHover={{ scale: 1.08 }}
        >
          {/* Image */}
          <img
            src={image}
            alt={name}
            className={`w-full h-full object-cover transition-all duration-500 ${
              video_url && isHovered ? 'opacity-0' : 'grayscale group-hover:grayscale-0'
            }`}
          />

          {/* Video (plays on hover) */}
          {video_url && (
            <video
              ref={videoRef}
              muted
              loop
              playsInline
              preload="metadata"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <source src={video_url} type="video/mp4" />
            </video>
          )}
          
          {/* TARGET Crosshair Overlay */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Outer ring with pulse */}
            <motion.div
              className="absolute inset-1 border-2 border-vav-gold/60 rounded-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ 
                scale: isHovered ? [1, 1.1, 1] : 0.8, 
                opacity: isHovered ? 1 : 0 
              }}
              transition={{ 
                scale: { duration: 1.5, repeat: Infinity },
                opacity: { duration: 0.3 }
              }}
            />
            {/* Middle ring */}
            <motion.div
              className="absolute inset-4 border border-vav-gold/40 rounded-full"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: isHovered ? 1 : 0.5, opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            />
            {/* Inner ring */}
            <motion.div
              className="absolute inset-8 border border-vav-gold/30 rounded-full"
              initial={{ scale: 0.3, opacity: 0 }}
              animate={{ scale: isHovered ? 1 : 0.3, opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            />
            {/* Center dot with pulse */}
            <motion.div
              className="absolute top-1/2 left-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-vav-gold rounded-full"
              initial={{ scale: 0 }}
              animate={{ 
                scale: isHovered ? [1, 1.3, 1] : 0,
                boxShadow: isHovered ? ['0 0 0 0 rgba(196,168,75,0.4)', '0 0 0 8px rgba(196,168,75,0)', '0 0 0 0 rgba(196,168,75,0.4)'] : 'none'
              }}
              transition={{ 
                scale: { duration: 1, repeat: Infinity },
                boxShadow: { duration: 1, repeat: Infinity },
                delay: 0.2 
              }}
            />
            {/* Crosshairs */}
            <motion.div
              className="absolute top-1/2 left-0 right-0 h-px bg-vav-gold/40"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: isHovered ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="absolute left-1/2 top-0 bottom-0 w-px bg-vav-gold/40"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: isHovered ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            />
            {/* Rotating scan line */}
            <motion.div
              className="absolute inset-0 overflow-hidden rounded-full"
              animate={{ rotate: isHovered ? 360 : 0 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <div className="absolute top-1/2 left-1/2 w-1/2 h-px bg-gradient-to-r from-vav-gold/60 to-transparent origin-left" />
            </motion.div>
          </motion.div>
          
          {/* Glow effect */}
          <motion.div
            className="absolute inset-0 rounded-full pointer-events-none"
            animate={{
              boxShadow: isHovered 
                ? 'inset 0 0 40px rgba(196, 168, 75, 0.5), 0 0 30px rgba(196, 168, 75, 0.3)' 
                : 'inset 0 0 0 rgba(196, 168, 75, 0)',
            }}
            transition={{ duration: 0.3 }}
          />

          {/* Video indicator */}
          {video_url && !isHovered && (
            <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center">
              <Play className="w-2.5 h-2.5 text-white fill-white" />
            </div>
          )}
        </motion.div>
        
        {/* Name with hover effect */}
        <motion.h3 
          className="font-heading text-sm uppercase tracking-wider text-vav-fg group-hover:text-vav-gold transition-colors flex items-center gap-1"
          animate={{ y: isHovered ? -2 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {name}
          {external_url && (
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </motion.h3>
        
        {count && (
          <motion.span 
            className="text-xs text-vav-fg-muted mt-1"
            animate={{ opacity: isHovered ? 0 : 1 }}
            transition={{ duration: 0.2 }}
          >
            {count} Ürün
          </motion.span>
        )}
      </a>
    </motion.div>
  );
};

// Collection Card Component
export const CollectionCard = ({ title, image, link, count, className = '' }) => {
  return (
    <Link
      to={link}
      className={`group relative block overflow-hidden rounded-2xl aspect-[4/5] ${className}`}
      data-testid={`collection-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Background Image */}
      <motion.div 
        className="absolute inset-0"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.6 }}
      >
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
      </motion.div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <TechLabel variant="default" className="mb-3">
          {count}
        </TechLabel>
        <h3 className="font-heading text-2xl text-white group-hover:text-vav-accent-cyan transition-colors">
          {title}
        </h3>
      </div>

      {/* Border glow */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        initial={{ boxShadow: 'inset 0 0 0 1px rgba(39, 39, 42, 1)' }}
        whileHover={{ boxShadow: 'inset 0 0 0 2px rgba(0, 240, 255, 0.5)' }}
        transition={{ duration: 0.3 }}
      />
    </Link>
  );
};

// Technology Card Component
export const TechnologyCard = ({ tech, index = 0 }) => {
  const iconMap = {
    Droplets: require('lucide-react').Droplets,
    Wind: require('lucide-react').Wind,
    Shield: require('lucide-react').Shield,
    Zap: require('lucide-react').Zap,
    Mountain: require('lucide-react').Mountain,
    Thermometer: require('lucide-react').Thermometer,
  };
  
  const Icon = iconMap[tech.icon] || iconMap.Shield;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Link
        to={`/technology/${tech.slug}`}
        className="group flex flex-col items-center text-center p-6 rounded-2xl bg-vav-bg-paper border border-vav-border hover:border-current transition-all duration-300"
        style={{ '--hover-color': tech.color }}
        data-testid={`tech-card-${tech.slug}`}
      >
        <div 
          className="w-16 h-16 flex items-center justify-center mb-4 rounded-xl border border-current/30 group-hover:bg-current/10 transition-all duration-300"
          style={{ color: tech.color }}
        >
          <Icon className="w-8 h-8" />
        </div>
        
        <h3 
          className="font-heading text-lg text-vav-fg group-hover:text-current transition-colors"
          style={{ '--tw-text-opacity': 1 }}
        >
          <span className="group-hover:text-[var(--hover-color)]">{tech.name}</span>
        </h3>
        
        <p className="text-sm text-vav-fg-muted mt-2 line-clamp-2">
          {tech.description}
        </p>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
